/**
 * Shop catalog helpers.
 *
 * Every purchasable product and its USD price comes from the backend
 * catalog (`GET /api/storefront/catalog/`). These selectors pick the
 * product a given shop page sells so pages never hard-code prices or
 * product ids.
 */

import { useStorefrontCatalog } from '@/shared/api/generated/storefront-catalog/storefront-catalog'

import type { CatalogBook, StorefrontCatalog } from '@/shared/api/models'

const CATALOG_STALE_TIME = 5 * 60 * 1000 // 5 minutes

export type BookCoverType = 'hardcover' | 'softcover'

export function useShopCatalog() {
  return useStorefrontCatalog({
    query: { staleTime: CATALOG_STALE_TIME },
  })
}

/** The book the catalog marks as default (used for bundles and "$X hardcover" copy). */
export function selectDefaultBook(catalog: StorefrontCatalog | undefined): CatalogBook | undefined {
  if (!catalog) return undefined

  return catalog.books.find((book) => book.preset_id === catalog.default_book_preset_id)
    ?? catalog.books.find((book) => book.is_default)
}

/**
 * The book sold on a cover-type product page. Hardcover prefers the default
 * preset when it is a hardcover; otherwise the first book of that cover type.
 */
export function selectBookByCoverType(
  catalog: StorefrontCatalog | undefined,
  coverType: BookCoverType,
): CatalogBook | undefined {
  if (!catalog) return undefined

  const defaultBook = selectDefaultBook(catalog)

  if (defaultBook?.cover_type === coverType) return defaultBook

  return catalog.books.find((book) => book.cover_type === coverType)
}

/** True when the compare-at price is a real markdown over the selling price. */
export function hasCompareAtDiscount(price: string | null | undefined, compareAt: string | null | undefined): boolean {
  if (!price || !compareAt) return false

  return parseFloat(compareAt) > parseFloat(price)
}
